import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitGroupDto } from "../../../../domain/DataKitDto";
import "./DataKitDrawer.scss";
import "./DataKitGroupDrawer.scss";
import { useDrawerDismiss } from "./useDrawerDismiss";
import TextArea from "antd/lib/input/TextArea";

const pathOf = (group: DataKitGroupDto) =>
  group.path && group.path.length > 0 ? group.path : [group.name];

interface Props {
  kit: DataKitDto;
  group: DataKitGroupDto;
  groups: DataKitGroupDto[];
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
  /** Navigates the drawer to another group — how the breadcrumb and the nested rows move around. */
  onNavigate: (group: DataKitGroupDto) => void;
}

/**
 * Edit one group: its name, its note, and the groups nested directly under it.
 *
 * Groups nest to any depth, so this drawer is navigable rather than flat — the breadcrumb walks
 * back up the ancestry and a nested row walks down into it. That replaces the one-level
 * "subgroups" list the design was drawn against, per the decision that a subgroup is simply a
 * group with a parent.
 */
export function DataKitGroupDrawer({
  kit,
  group,
  groups,
  canWrite,
  onClose,
  onChanged,
  onNavigate,
}: Props) {
  const path = pathOf(group);

  const [name, setName] = useState(group.name);
  const [note, setNote] = useState(group.note || "");
  const [childDraft, setChildDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<DataKitGroupDto | null>(null);

  useEffect(() => {
    setName(group.name);
    setNote(group.note || "");
    setChildDraft("");
  }, [group]);

  const { closing, dismiss } = useDrawerDismiss();
  const close = useCallback(() => dismiss(onClose), [dismiss, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !confirmDelete) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close, confirmDelete]);

  /** Ancestors, root first, excluding this group — the breadcrumb. */
  const ancestors = useMemo(
    () =>
      path
        .slice(0, -1)
        .map((_, i) => {
          const prefix = path.slice(0, i + 1).join("\u001f");
          return groups.find((g) => pathOf(g).join("\u001f") === prefix);
        })
        .filter((g): g is DataKitGroupDto => !!g),
    [path, groups]
  );

  /** Groups sitting directly under this one. */
  const children = useMemo(() => {
    const own = path.join("\u001f");
    return groups.filter((g) => {
      const p = pathOf(g);
      return p.length === path.length + 1 && p.slice(0, path.length).join("\u001f") === own;
    });
  }, [groups, path]);

  const saveDetails = async () => {
    const trimmed = name.trim();
    if (!trimmed || !kit.id) return;
    setSaving(true);
    try {
      if (trimmed !== group.name) {
        await DataKitApi.renameGroup(kit.id, { name: group.name, newName: trimmed, path });
      }
      const trimmedNote = note.trim();
      if (trimmedNote !== (group.note || "")) {
        const renamedPath = [...path.slice(0, -1), trimmed];
        await DataKitApi.setGroupNote(kit.id, {
          name: trimmed,
          note: trimmedNote || null,
          path: renamedPath,
        });
      }
      onChanged();
      close();
    } catch {
      message.error("Could not save this group");
    } finally {
      setSaving(false);
    }
  };

  const addChild = () => {
    const value = childDraft.trim();
    if (!value || !kit.id) return;
    setSaving(true);
    DataKitApi.createGroup(kit.id, { groupName: value, name: value, path: [...path, value] })
      .then(() => {
        setChildDraft("");
        onChanged();
      })
      .catch(() => message.error("Could not add that group"))
      .finally(() => setSaving(false));
  };

  const removeChild = () => {
    if (!confirmDelete || !kit.id) return;
    setSaving(true);
    DataKitApi.deleteGroup(kit.id, confirmDelete.name, pathOf(confirmDelete))
      .then(() => {
        setConfirmDelete(null);
        onChanged();
      })
      .catch(() =>
        message.error("Could not delete that group - move its items and nested groups out first")
      )
      .finally(() => setSaving(false));
  };

  return (
    <>
      <div className={`data-kit-drawer-scrim${closing ? " closing" : ""}`} onClick={close} />
      <aside
        className={`data-kit-group-drawer${closing ? " closing" : ""}`}
        role="dialog"
        aria-label={`Edit ${group.name}`}
      >
        <header className="data-kit-group-drawer-header">
          <div className="group-drawer-heading">
            {ancestors.length > 0 && (
              <nav className="group-drawer-crumbs">
                {ancestors.map((ancestor) => (
                  <button
                    key={pathOf(ancestor).join("\u001f")}
                    type="button"
                    onClick={() => onNavigate(ancestor)}
                  >
                    {ancestor.name}
                    <RightOutlined className="group-drawer-crumb-sep" />
                  </button>
                ))}
              </nav>
            )}
            <h2>{name || "Group"}</h2>
          </div>
          <button type="button" className="data-kit-drawer-close" onClick={close} aria-label="Close">
            <CloseOutlined />
          </button>
        </header>

        <div className="data-kit-group-drawer-body">
          <p className="group-drawer-hint">Name and note reach every design that uses it.</p>

          <label className="data-kit-field">
            <span>Group Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canWrite}
              maxLength={255}
              autoFocus
            />
          </label>

          <label className="data-kit-field data-kit-group-note-field">
            <span>Group Note</span>
            <TextArea
              placeholder="Group note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={!canWrite}
              maxLength={500}
            />
          </label>

          <div className="data-kit-field">
            <div className="group-drawer-nested-copy">
              <span>Groups Nested Under {group.name}</span>
              <span className="group-drawer-nested-hint">
                Optional &mdash; appear as headers on designs
              </span>
            </div>

            {children.length > 0 && (
              <div className="group-drawer-children">
                {children.map((child) => (
                  <div className="group-drawer-child" key={pathOf(child).join("\u001f")}>
                    <button
                      type="button"
                      className="group-drawer-child-name"
                      onClick={() => onNavigate(child)}
                    >
                      {child.name}
                    </button>
                    <span className="group-drawer-child-count">
                      {child.itemCount} item{child.itemCount === 1 ? "" : "s"}
                    </span>
                    {canWrite && (
                      <button
                        type="button"
                        className="group-drawer-child-remove"
                        onClick={() => setConfirmDelete(child)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canWrite && (
              <div className="group-drawer-add">
                <Input
                  placeholder="Nested group"
                  value={childDraft}
                  onChange={(e) => setChildDraft(e.target.value)}
                  onPressEnter={addChild}
                  maxLength={255}
                />
                <Button className="secondary" onClick={addChild} disabled={!childDraft.trim() || saving}>
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>

        <footer className="data-kit-drawer-footer">
          {/* No Cancel: the header X closes, exactly as Cancel did (Lyra, Oct 1). */}
          <div className="data-kit-drawer-footer-buttons">
            <Button
              type="primary"
              onClick={saveDetails}
              loading={saving}
              disabled={!canWrite || !name.trim()}
            >
              Save
            </Button>
          </div>
        </footer>
      </aside>

      <Modal
        className={"clean one"}
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
      >
        <header>
          <h2>Delete {confirmDelete?.name || ""}?</h2>
        </header>
        <p className="data-kit-discard-copy">
          Only possible when nothing is filed under it. Items and nested groups have to be moved
          somewhere else first.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button size="large" className="primary" loading={saving} onClick={removeChild}>
            Delete
          </Button>
        </footer>
      </Modal>
    </>
  );
}
